import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { JsonLd } from '@/components/JsonLd';
import { blogArticles } from '@/i18n/blogArticles';
import { blogContent } from '@/i18n/blogContent';

export default function BlogArticle() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { language } = useLanguage();

  // Get article metadata from blogArticles
  const articlesList = blogArticles[language] || blogArticles.fr;
  const articleMeta = articlesList.find(a => a.slug === slug);
  
  // Get article content from blogContent
  const articleContent = blogContent[slug]?.[language] || blogContent[slug]?.fr;

  if (!articleMeta || !articleContent) {
    return (
      <div className="min-h-screen py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">Article non trouvé</h1>
          <button onClick={() => navigate(`/${language}/blog`)} className="btn-primary">
            Retour au blog
          </button>
        </div>
      </div>
    );
  }

  const article = {
    ...articleMeta,
    ...articleContent
  };

  // Localized email-capture / signup CTA shown at the bottom of every article
  const CTA = {
    fr: {
      heading: "Trouvez votre match en 2 min",
      subtext: "Rejoignez les milliers de célibataires qui font confiance à notre IA pour trouver l'amour. Gratuit pour les 500 premiers inscrits.",
      button: "Commencer gratuitement",
      note: "Sans engagement · Paiement unique · Données protégées (RGPD)",
    },
    en: {
      heading: "Find your match in 2 minutes",
      subtext: "Join thousands of singles who trust our AI to find love. Free for the first 500 sign-ups.",
      button: "Start for free",
      note: "No commitment · One-time payment · GDPR-protected data",
    },
    es: {
      heading: "Encuentra tu match en 2 minutos",
      subtext: "Únete a miles de solteros que confían en nuestra IA para encontrar el amor. Gratis para los primeros 500 registros.",
      button: "Empezar gratis",
      note: "Sin compromiso · Pago único · Datos protegidos (RGPD)",
    },
    de: {
      heading: "Finde dein Match in 2 Minuten",
      subtext: "Schließe dich Tausenden Singles an, die unserer KI vertrauen, um die Liebe zu finden. Kostenlos für die ersten 500 Anmeldungen.",
      button: "Kostenlos starten",
      note: "Keine Verpflichtung · Einmalzahlung · DSGVO-geschützte Daten",
    },
  };
  const cta = CTA[language] || CTA.fr;

  // Fire a Google Analytics (GA4) + GTM dataLayer event when a signup CTA is clicked
  const trackSignupClick = (location) => {
    const payload = {
      event: 'cta_signup_click',
      cta_location: location,
      article_slug: slug,
      language: language,
    };
    try {
      if (typeof window !== 'undefined') {
        // GTM custom event (configure a trigger on "cta_signup_click")
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push(payload);
        }
        // Direct GA4 event (works without extra GTM config)
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'cta_signup_click', {
            cta_location: location,
            article_slug: slug,
            language: language,
          });
        }
      }
    } catch (e) {
      // analytics must never block navigation
    }
  };

  // OLD articles object removed - now using blogContent from i18n
  /* const articles = {
    "intelligence-artificielle-revolutionne-rencontres": {
      title: "Comment l'Intelligence Artificielle Révolutionne les Rencontres en Ligne",
      excerpt: "Découvrez comment l'IA transforme le monde du dating en analysant la personnalité pour créer des matchs plus authentiques et durables.",
      date: "30 Janvier 2025",
      readTime: "5 min",
      category: "Technologie",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=80",
      content: `
        <p>Le monde des rencontres en ligne a considérablement évolué au cours de la dernière décennie. Mais aujourd'hui, nous assistons à une véritable révolution grâce à l'intelligence artificielle.</p>

        <h2>L'Ère du Swipe est Révolue</h2>
        <p>Pendant des années, les applications de rencontre se sont basées sur un principe simple : swiper à droite ou à gauche en fonction de photos. Cette approche superficielle a montré ses limites. Les utilisateurs passent des heures à swiper sans trouver de connexions significatives.</p>

        <p>L'intelligence artificielle change la donne en analysant bien plus que des photos. Elle comprend votre personnalité, vos valeurs, vos aspirations et vos modes de communication pour identifier les personnes avec lesquelles vous avez de vraies chances de construire quelque chose de durable.</p>

        <h2>Comment l'IA Analyse Votre Personnalité</h2>
        <p>Chez A-More AI, nous utilisons Claude, une intelligence artificielle de pointe développée par Anthropic. Cette technologie analyse votre questionnaire de personnalité en profondeur, en évaluant :</p>

        <ul>
          <li><strong>Vos valeurs fondamentales</strong> : Ce qui compte vraiment pour vous dans la vie</li>
          <li><strong>Votre style de communication</strong> : Comment vous exprimez vos émotions et vos besoins</li>
          <li><strong>Vos objectifs de vie</strong> : Où vous vous voyez dans 5, 10 ans</li>
          <li><strong>Votre vision relationnelle</strong> : Ce que vous recherchez dans une relation</li>
          <li><strong>Votre gestion des conflits</strong> : Comment vous abordez les désaccords</li>
        </ul>

        <h2>Des Matchs Plus Pertinents</h2>
        <p>Grâce à cette analyse multi-dimensionnelle, l'IA peut calculer un score de compatibilité précis entre vous et d'autres utilisateurs. Ce score ne se base pas sur des critères superficiels, mais sur une véritable compatibilité de personnalité.</p>

        <p>Les résultats parlent d'eux-mêmes : nos utilisateurs rapportent des taux de satisfaction 3 fois supérieurs aux applications traditionnelles, avec des conversations plus longues et des rendez-vous plus réussis.</p>

        <h2>L'Avenir des Rencontres</h2>
        <p>L'intelligence artificielle dans les rencontres n'en est qu'à ses débuts. Dans un futur proche, nous verrons des IA capables de :</p>

        <ul>
          <li>Suggérer des activités de premier rendez-vous parfaitement adaptées aux deux personnes</li>
          <li>Identifier les meilleurs moments pour relancer une conversation</li>
          <li>Détecter les incompatibilités majeures avant même le premier message</li>
          <li>Améliorer continuellement les recommandations grâce à l'apprentissage machine</li>
        </ul>

        <h2>Conclusion</h2>
        <p>L'intelligence artificielle ne remplace pas l'émotion humaine, mais elle l'aide à s'exprimer au bon endroit, au bon moment, avec les bonnes personnes. C'est une révolution silencieuse qui est en train de transformer des millions de vies.</p>

        <p>Êtes-vous prêt à découvrir comment l'IA peut vous aider à trouver l'amour ? <a href="/auth?mode=signup">Rejoignez A-More AI gratuitement</a> et faites l'expérience du matching intelligent.</p>
      `
    },
    "conseils-premier-rendez-vous-reussi": {
      title: "10 Conseils pour un Premier Rendez-Vous Réussi",
      excerpt: "Des astuces pratiques pour faire bonne impression lors de votre premier rendez-vous et créer une connexion authentique dès le départ.",
      date: "28 Janvier 2025",
      readTime: "7 min",
      category: "Conseils",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200&q=80",
      content: `
        <p>Le premier rendez-vous peut être source d'anxiété, mais c'est aussi une opportunité excitante de créer une connexion authentique avec quelqu'un. Voici nos 10 meilleurs conseils pour faire de votre premier rendez-vous un succès.</p>

        <h2>1. Choisissez le Bon Lieu</h2>
        <p>Privilégiez un endroit public, confortable et propice à la conversation. Un café cosy, un restaurant avec une ambiance calme ou une promenade dans un parc sont d'excellents choix. Évitez les lieux trop bruyants où vous devrez crier pour vous entendre.</p>

        <h2>2. Soyez Ponctuel(le)</h2>
        <p>Arriver à l'heure montre du respect pour le temps de l'autre personne. Si vous êtes en retard, prévenez immédiatement. La ponctualité est un signe de maturité et d'intérêt.</p>

        <h2>3. Soignez Votre Apparence</h2>
        <p>Vous n'avez pas besoin d'un costume trois-pièces, mais présentez-vous sous votre meilleur jour. Portez des vêtements propres et repassés dans lesquels vous vous sentez à l'aise et confiant(e).</p>

        <h2>4. Posez des Questions Ouvertes</h2>
        <p>Au lieu de questions fermées (oui/non), posez des questions qui encouragent la conversation : "Qu'est-ce qui te passionne dans ton travail ?" plutôt que "Tu aimes ton travail ?"</p>

        <h2>5. Écoutez Activement</h2>
        <p>Ne pensez pas seulement à ce que vous allez dire ensuite. Écoutez vraiment ce que votre rendez-vous dit, posez des questions de suivi et montrez un intérêt sincère.</p>

        <h2>6. Soyez Vous-Même</h2>
        <p>L'authenticité est attractive. N'essayez pas d'être quelqu'un d'autre ou de présenter une version idéalisée de vous-même. Si ça ne clique pas, c'est OK - mieux vaut le savoir dès le début.</p>

        <h2>7. Rangez Votre Téléphone</h2>
        <p>Mettez votre téléphone en mode silencieux et rangez-le. Rien ne dit "je ne suis pas intéressé(e)" plus clairement que de consulter son téléphone pendant un rendez-vous.</p>

        <h2>8. Partagez l'Addition</h2>
        <p>À moins que quelqu'un ait explicitement proposé d'inviter, proposez de partager l'addition. C'est équitable et enlève toute pression ou attente implicite.</p>

        <h2>9. Gardez le Sujet Léger</h2>
        <p>Évitez les sujets controversés ou trop personnels lors d'un premier rendez-vous : politique, religion, ex-partenaires, problèmes financiers. Concentrez-vous sur le présent et découvrez-vous mutuellement.</p>

        <h2>10. Terminez Sur une Note Positive</h2>
        <p>Si le rendez-vous s'est bien passé, exprimez-le : "J'ai vraiment passé un bon moment". Si vous souhaitez vous revoir, dites-le clairement : "J'aimerais beaucoup qu'on se revoie". La clarté évite les malentendus.</p>

        <h2>Bonus : Donnez un Retour Après</h2>
        <p>Que vous soyez intéressé(e) ou non, un message courtois après le rendez-vous est toujours apprécié. Un simple "Merci pour ce moment" ou "J'ai passé une excellente soirée" montre de la considération.</p>

        <h2>Conclusion</h2>
        <p>Le premier rendez-vous n'a pas besoin d'être parfait. Ce qui compte, c'est d'être authentique, respectueux et d'avoir du plaisir. Avec ces conseils, vous êtes prêt(e) à créer une première impression mémorable.</p>

        <p>Besoin de trouver la personne idéale pour mettre ces conseils en pratique ? <a href="/auth?mode=signup">Rejoignez A-More AI</a> et laissez notre IA vous aider à trouver des matchs compatibles.</p>
      `
    },
    "psychologie-compatibilite-amoureuse": {
      title: "La Psychologie de la Compatibilité Amoureuse",
      excerpt: "Qu'est-ce qui fait qu'une relation fonctionne ? Explorez les bases scientifiques de la compatibilité entre deux personnes.",
      date: "25 Janvier 2025",
      readTime: "8 min",
      category: "Psychologie",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1200&q=80",
      content: `
        <p>Qu'est-ce qui fait qu'une relation amoureuse fonctionne sur le long terme ? La psychologie moderne nous donne des réponses fascinantes basées sur des décennies de recherche.</p>

        <h2>Les 5 Piliers de la Compatibilité</h2>
        <p>Selon les recherches en psychologie relationnelle, la compatibilité repose sur cinq piliers fondamentaux :</p>

        <h3>1. Les Valeurs Partagées</h3>
        <p>Les couples qui partagent des valeurs fondamentales similaires (famille, carrière, spiritualité, honnêteté) ont significativement plus de chances de rester ensemble. Ces valeurs agissent comme une boussole commune qui guide les décisions importantes de la vie.</p>

        <h3>2. Le Style d'Attachement</h3>
        <p>La théorie de l'attachement montre que nous avons tous un "style d'attachement" développé dans l'enfance : sécure, anxieux ou évitant. Les couples avec des styles d'attachement compatibles ont moins de conflits et plus de satisfaction relationnelle.</p>

        <h3>3. La Communication</h3>
        <p>Ce n'est pas seulement ce que vous dites, mais comment vous le dites. Les couples compatibles ont souvent des styles de communication similaires ou complémentaires. Ils savent exprimer leurs besoins et écouter ceux de l'autre sans jugement.</p>

        <h3>4. Les Objectifs de Vie</h3>
        <p>Vouloir des enfants ou non, préférer vivre en ville ou à la campagne, accorder plus d'importance à la carrière ou à la famille - ces objectifs fondamentaux doivent être alignés pour qu'une relation fonctionne.</p>

        <h3>5. L'Intelligence Émotionnelle</h3>
        <p>La capacité à comprendre et gérer ses propres émotions, ainsi qu'à empathiser avec celles du partenaire, est cruciale. L'intelligence émotionnelle permet de naviguer les inévitables conflits d'une relation de manière constructive.</p>

        <h2>Le Mythe de l'Âme Sœur</h2>
        <p>La recherche psychologique démystifie l'idée romantique d'une "âme sœur unique". En réalité, il existe de nombreuses personnes avec lesquelles nous pourrions être compatibles. La clé est de trouver quelqu'un avec qui nous partageons suffisamment de ces piliers fondamentaux.</p>

        <p>Dr. John Gottman, célèbre chercheur en relations, a découvert qu'il peut prédire avec 94% de précision si un couple va divorcer simplement en observant comment ils gèrent les conflits. La compatibilité n'est pas l'absence de différences, mais la capacité à les gérer sainement.</p>

        <h2>L'Importance de la Complémentarité</h2>
        <p>Contrairement à la croyance populaire, "les opposés s'attirent" n'est que partiellement vrai. La recherche montre que nous sommes plus heureux avec des partenaires similaires à nous sur les aspects fondamentaux (valeurs, objectifs), mais qui nous complètent sur des traits de personnalité secondaires.</p>

        <p>Par exemple, un introverti peut très bien s'entendre avec un extraverti si leurs valeurs fondamentales sont alignées. L'extraverti aide l'introverti à sortir de sa zone de confort, tandis que l'introverti apporte calme et réflexion.</p>

        <h2>Comment A-More AI Utilise Cette Science</h2>
        <p>Notre questionnaire de personnalité est conçu sur ces bases scientifiques. Nous analysons :</p>
        <ul>
          <li>Vos valeurs fondamentales et priorités de vie</li>
          <li>Votre style de communication et d'expression émotionnelle</li>
          <li>Vos objectifs à court et long terme</li>
          <li>Votre approche des conflits et des compromis</li>
          <li>Votre style d'attachement relationnel</li>
        </ul>

        <p>L'IA Claude croise ensuite ces données pour identifier les profils les plus compatibles avec vous, en tenant compte à la fois des similarités essentielles et des complémentarités bénéfiques.</p>

        <h2>Conclusion</h2>
        <p>La compatibilité amoureuse n'est pas magique - c'est une science. En comprenant ce qui fait vraiment fonctionner une relation, nous pouvons faire des choix plus éclairés et construire des partenariats plus solides et plus satisfaisants.</p>

        <p>Prêt à trouver quelqu'un de vraiment compatible avec vous ? <a href="/auth?mode=signup">Essayez A-More AI gratuitement</a> et découvrez la puissance du matching basé sur la science psychologique.</p>
      `
    },
    "erreurs-eviter-rencontres-en-ligne": {
      title: "Les 7 Erreurs à Éviter dans les Rencontres en Ligne",
      excerpt: "Découvrez les erreurs les plus communes que font les utilisateurs d'applications de rencontre et comment les éviter pour maximiser vos chances.",
      date: "22 Janvier 2025",
      readTime: "6 min",
      category: "Conseils",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1200&q=80",
      content: `
        <p>Les rencontres en ligne offrent des opportunités infinies, mais certaines erreurs peuvent sérieusement réduire vos chances de succès. Voici les 7 erreurs les plus courantes et comment les éviter.</p>

        <h2>1. Négliger Son Profil</h2>
        <p>Votre profil est votre vitrine. Une bio vide ou des photos floues donnent l'impression que vous n'êtes pas sérieux(se). Prenez le temps de :</p>
        <ul>
          <li>Rédiger une bio authentique et engageante</li>
          <li>Choisir des photos récentes et de qualité</li>
          <li>Montrer différentes facettes de votre personnalité</li>
          <li>Être honnête sur qui vous êtes et ce que vous recherchez</li>
        </ul>
        <p><strong>Solution :</strong> Consacrez au moins 30 minutes à optimiser votre profil. Sur A-More AI, le questionnaire de personnalité approfondi compense un profil visuel moins travaillé, mais soignez quand même votre présentation !</p>

        <h2>2. Être Trop Sélectif(ve) ou Pas Assez</h2>
        <p>Swiper à droite sur tous les profils par désespoir ou être trop exigeant(e) sont deux extrêmes contre-productifs.</p>
        <p><strong>Trop sélectif :</strong> Vous ratez des connexions potentielles inattendues. Parfois, la compatibilité n'est pas évidente sur une photo.</p>
        <p><strong>Pas assez sélectif :</strong> Vous perdez du temps avec des personnes totalement incompatibles et diluez vos chances avec les bons profils.</p>
        <p><strong>Solution :</strong> Sur A-More AI, l'IA calcule déjà la compatibilité pour vous. Concentrez-vous sur les profils avec un score élevé et donnez leur une vraie chance même si la photo n'est pas parfaite.</p>

        <h2>3. Commencer Par "Salut" ou "Ça va ?"</h2>
        <p>Les messages génériques sont ignorés 90% du temps. Ils montrent un manque d'effort et n'incitent pas à répondre.</p>
        <p><strong>Messages à éviter :</strong></p>
        <ul>
          <li>"Salut, ça va ?"</li>
          <li>"Hey 😊"</li>
          <li>"Tu es belle"</li>
          <li>Des copier-coller évidents</li>
        </ul>
        <p><strong>Solution :</strong> Personnalisez chaque message en vous référant au profil de la personne. Sur A-More AI, l'IA vous suggère des débuts de conversation basés sur vos points communs - utilisez-les ou inspirez-vous-en !</p>

        <h2>4. Attendre Trop Longtemps Avant de Se Rencontrer</h2>
        <p>Échanger pendant des semaines sans jamais se voir crée des attentes irréalistes et conduit souvent à la déception.</p>
        <p><strong>Le piège :</strong> Vous construisez une image idéalisée dans votre tête qui ne correspond pas à la réalité. De plus, l'intérêt peut s'estomper avec le temps.</p>
        <p><strong>Solution :</strong> Après 3-5 jours de bons échanges, proposez un rendez-vous. Un café de 30 minutes en vaut mille messages. Si la personne refuse systématiquement, c'est un red flag.</p>

        <h2>5. Ignorer les Red Flags</h2>
        <p>Par désir de trouver quelqu'un, beaucoup ignorent les signaux d'alarme évidents.</p>
        <p><strong>Red flags courants :</strong></p>
        <ul>
          <li>Refuse de passer à un appel vidéo (possible catfish)</li>
          <li>Profil vague sans vraies informations</li>
          <li>Demande d'argent ou de cadeaux</li>
          <li>Comportement agressif ou irrespectueux</li>
          <li>Incohérences dans ses histoires</li>
          <li>Refuse de se rencontrer en personne</li>
        </ul>
        <p><strong>Solution :</strong> Faites confiance à votre instinct. Si quelque chose vous dérange, signalez le profil et passez à autre chose. A-More AI vérifie les localisations et permet de signaler les comportements suspects.</p>

        <h2>6. Mettre Tous Ses Œufs Dans Le Même Panier</h2>
        <p>S'attacher émotionnellement à quelqu'un avant même de l'avoir rencontré(e) est une erreur classique.</p>
        <p><strong>Le problème :</strong> Vous investissez toute votre énergie émotionnelle dans une personne qui pourrait disparaître du jour au lendemain (ghosting) ou ne pas correspondre en personne.</p>
        <p><strong>Solution :</strong> Gardez plusieurs conversations actives jusqu'à ce que vous ayez rencontré quelqu'un et décidé de devenir exclusifs. Ce n'est pas de la manipulation, c'est de la prudence émotionnelle.</p>

        <h2>7. Mentir Sur Son Profil</h2>
        <p>Mentir sur son âge, sa taille, sa situation professionnelle ou utiliser de vieilles photos est contre-productif.</p>
        <p><strong>Pourquoi c'est une erreur :</strong></p>
        <ul>
          <li>La vérité finira par éclater au premier rendez-vous</li>
          <li>Cela tue instantanément la confiance</li>
          <li>Vous attirez des personnes qui ne vous correspondraient pas vraiment</li>
          <li>Vous construisez la relation sur des bases malsaines</li>
        </ul>
        <p><strong>Solution :</strong> Soyez authentique dès le départ. Les bonnes personnes vous aimeront pour qui vous êtes vraiment. Sur A-More AI, l'authenticité est valorisée - le système de notation sociale récompense les profils honnêtes.</p>

        <h2>Conclusion</h2>
        <p>Les rencontres en ligne peuvent être frustrantes, mais éviter ces 7 erreurs augmentera considérablement vos chances de succès. Rappelez-vous : la qualité prime sur la quantité, l'authenticité bat la perfection, et la patience est récompensée.</p>

        <p>Prêt(e) à démarrer sur de bonnes bases ? <a href="/auth?mode=signup">Rejoignez A-More AI</a> et laissez notre IA vous aider à éviter ces pièges tout en trouvant des matchs vraiment compatibles.</p>
      `
    },
    "creer-profil-dating-irresistible": {
      title: "Comment Créer un Profil de Dating Irrésistible",
      excerpt: "Guide complet pour rédiger une bio captivante, choisir les bonnes photos et se démarquer sur les applications de rencontre.",
      date: "20 Janvier 2025",
      readTime: "9 min",
      category: "Conseils",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=1200&q=80",
      content: `
        <p>Votre profil sur une application de rencontre est votre première impression - et souvent la seule chance que vous avez d'attirer l'attention. Voici comment créer un profil qui vous démarque vraiment.</p>

        <h2>Partie 1 : Les Photos Parfaites</h2>
        
        <h3>Photo Principale : Votre Atout Maître</h3>
        <p>Votre photo principale est décisive. 90% des utilisateurs décident de swiper droite ou gauche en moins de 3 secondes.</p>
        
        <p><strong>Caractéristiques d'une excellente photo principale :</strong></p>
        <ul>
          <li><strong>Votre visage clairement visible</strong> : Pas de lunettes de soleil, pas de chapeau couvrant</li>
          <li><strong>Sourire naturel</strong> : Les études montrent que le sourire augmente les matchs de 14%</li>
          <li><strong>Bonne qualité</strong> : Pas de photo floue ou pixelisée</li>
          <li><strong>Seul(e) sur la photo</strong> : Pas de confusion sur qui vous êtes</li>
          <li><strong>Arrière-plan neutre ou intéressant</strong> : Évitez le miroir de salle de bain</li>
          <li><strong>Photo récente</strong> : Datant de moins de 6 mois</li>
        </ul>

        <h3>Les Photos Supplémentaires : Racontez Votre Histoire</h3>
        <p>Ajoutez 4-6 photos variées qui montrent différentes facettes de vous :</p>
        
        <p><strong>1. Photo en action</strong> - Vous faisant une activité que vous aimez (sport, musique, cuisine)</p>
        <p><strong>2. Photo de groupe</strong> - Montre que vous êtes social(e), mais vous devez être facilement identifiable</p>
        <p><strong>3. Photo en voyage</strong> - Démontre votre ouverture d'esprit et lance des conversations</p>
        <p><strong>4. Photo avec un animal</strong> - Les photos avec des animaux augmentent les matchs de 19%</p>
        <p><strong>5. Photo habillé(e)</strong> - Montre que vous pouvez être élégant(e)</p>

        <p><strong>À ÉVITER :</strong></p>
        <ul>
          <li>Photos de groupe où on ne vous identifie pas</li>
          <li>Selfies dans le miroir de salle de bain</li>
          <li>Photos avec votre ex (même croppée !)</li>
          <li>Photos avec trop de filtres</li>
          <li>Toutes les photos dans le même lieu/tenue</li>
        </ul>

        <h2>Partie 2 : La Bio Parfaite</h2>

        <h3>Structure d'une Bio Efficace</h3>
        
        <p><strong>1. Accrochez en 1-2 phrases</strong></p>
        <p>Commencez par quelque chose d'unique, drôle ou intrigant :</p>
        <p><em>✅ "Architecte le jour, chef amateur le soir. Je fais les meilleurs tiramisus de Paris (certifié par ma grand-mère italienne)."</em></p>
        <p><em>❌ "J'aime voyager, les sorties entre amis et les séries."</em> (trop générique)</p>

        <p><strong>2. Parlez de Vos Passions (pas vos activités)</strong></p>
        <p>Ne listez pas "gym, voyages, cuisine". Expliquez POURQUOI :</p>
        <p><em>✅ "Je cours des marathons non pas pour la compétition, mais pour ces moments où l'esprit devient complètement clair."</em></p>

        <p><strong>3. Montrez Votre Personnalité</strong></p>
        <p>Utilisez l'humour, l'auto-dérision ou la vulnérabilité :</p>
        <p><em>✅ "Je parle 3 langues couramment mais je ne comprends toujours pas l'humour britannique."</em></p>

        <p><strong>4. Call-to-Action Subtil</strong></p>
        <p>Facilitez le début de conversation :</p>
        <p><em>✅ "Recommande-moi une série que je n'ai probablement pas vue, je te mets au défi."</em></p>

        <h3>Formules à Éviter Absolument</h3>
        <ul>
          <li>"Pas là pour des plans d'un soir" (négatif dès le départ)</li>
          <li>"Demande-moi" (paresseux)</li>
          <li>"Aventurier, spontané, passionné" (clichés sans substance)</li>
          <li>"Si tu ne supportes pas [trait négatif]..." (commence par la négativité)</li>
          <li>Listes de ce que vous NE voulez PAS (toxique)</li>
        </ul>

        <h2>Partie 3 : Le Questionnaire de Personnalité (A-More AI)</h2>

        <p>Sur A-More AI, le questionnaire de personnalité est votre arme secrète. Contrairement aux apps traditionnelles, vos réponses pèsent plus que vos photos.</p>

        <h3>Conseils pour le Questionnaire :</h3>
        <ul>
          <li><strong>Soyez 100% honnête</strong> - L'IA détecte les incohérences et ça affectera la qualité de vos matchs</li>
          <li><strong>Prenez votre temps</strong> - 15-20 minutes investies = des mois de meilleurs matchs</li>
          <li><strong>Répondez ce que vous ÊTES, pas ce que vous aimeriez être</strong></li>
          <li><strong>Soyez spécifique</strong> - "J'aime lire" vs "Je dévore des romans policiers scandinaves"</li>
        </ul>

        <h2>Partie 4 : Les Prompts et Questions</h2>

        <p>Si votre app propose des prompts (questions à répondre), choisissez ceux qui :</p>
        <ul>
          <li>Montrent votre personnalité unique</li>
          <li>Lancent des conversations</li>
          <li>Révèlent vos valeurs</li>
        </ul>

        <p><strong>Exemples de bonnes réponses :</strong></p>
        <p><em>Prompt : "Un fait surprenant à mon sujet"</em><br/>
        ✅ "J'ai vécu 6 mois dans un monastère tibétain. Oui, j'étais sérieux à ce point-là après ma rupture."</p>

        <p><em>Prompt : "Mon spot secret préféré"</em><br/>
        ✅ "Une librairie cachée rue Saint-André-des-Arts qui sert du thé et où personne ne parle. Le paradis introverti."</p>

        <h2>Partie 5 : Ce Que Disent les Stats</h2>

        <p>Des études sur des millions de profils révèlent :</p>
        <ul>
          <li>Les profils avec 6 photos reçoivent 47% de matchs en plus que ceux avec 3 photos</li>
          <li>Mentionner des hobbies spécifiques augmente les messages de 34%</li>
          <li>Une bio de 300-500 caractères est l'idéal (assez pour intriguer, pas trop pour ennuyer)</li>
          <li>Les profils qui utilisent l'humour reçoivent 30% plus de messages</li>
          <li>Être vulnérable ou authentique bat "essayer d'impressionner" à chaque fois</li>
        </ul>

        <h2>Erreurs Qui Tuent Votre Profil</h2>
        <ul>
          <li><strong>Photos de groupe uniquement</strong> - Personne ne veut jouer à "Où est Charlie"</li>
          <li><strong>Pas de bio</strong> - Montre un manque d'effort</li>
          <li><strong>Bio négative</strong> - "Pas de drama", "Si tu es comme mon ex..."</li>
          <li><strong>Exigences trop spécifiques</strong> - "Doit mesurer 1m85, aimer le CrossFit et les chats"</li>
          <li><strong>Mensonges</strong> - Fausses photos, faux âge = perte de confiance instantanée</li>
        </ul>

        <h2>Checklist Finale : Votre Profil Est-il Prêt ?</h2>
        <p>Avant de valider votre profil, vérifiez :</p>
        <ul>
          <li>☐ 5-6 photos variées et de qualité</li>
          <li>☐ Photo principale claire avec votre visage souriant</li>
          <li>☐ Bio de 300-500 caractères authentique et engageante</li>
          <li>☐ Mention d'au moins 2-3 hobbies spécifiques</li>
          <li>☐ Pas de négativité dans la bio</li>
          <li>☐ Call-to-action subtil pour faciliter les conversations</li>
          <li>☐ Aucun mensonge sur votre âge, taille, situation</li>
          <li>☐ Questionnaire de personnalité complété honnêtement (A-More AI)</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Un profil irrésistible n'est pas parfait - il est authentique, engageant et vous représente vraiment. Investissez le temps nécessaire pour le créer, car c'est votre carte de visite dans le monde des rencontres en ligne.</p>

        <p>Prêt(e) à créer votre profil sur A-More AI ? <a href="/auth?mode=signup">Inscrivez-vous gratuitement</a> et laissez notre IA vous aider à vous présenter sous votre meilleur jour tout en trouvant des personnes vraiment compatibles.</p>
      `
    },
    "signes-relation-saine-equilibree": {
      title: "Les Signes d'une Relation Saine et Équilibrée",
      excerpt: "Comment reconnaître une relation épanouissante ? Découvrez les indicateurs d'une relation saine basée sur le respect mutuel.",
      date: "18 Janvier 2025",
      readTime: "7 min",
      category: "Psychologie",
      author: "Équipe A-More AI",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&q=80",
      content: `
        <p>Comment savoir si votre relation est saine ou toxique ? Découvrez les signes d'une relation équilibrée et épanouissante basée sur des décennies de recherche en psychologie relationnelle.</p>

        <h2>1. Communication Ouverte et Honnête</h2>
        <p>Dans une relation saine, vous pouvez parler de tout sans craindre le jugement ou la colère.</p>

        <p><strong>Signes d'une bonne communication :</strong></p>
        <ul>
          <li>Vous vous sentez écouté(e) quand vous parlez</li>
          <li>Votre partenaire pose des questions pour mieux comprendre</li>
          <li>Les désaccords se résolvent par la discussion, pas les cris</li>
          <li>Vous pouvez exprimer vos besoins sans vous sentir égoïste</li>
          <li>Les deux partenaires admettent leurs erreurs</li>
        </ul>

        <p><strong>Red flag :</strong> Vous marchez sur des œufs pour éviter certains sujets, ou votre partenaire refuse systématiquement de discuter de problèmes importants.</p>

        <h2>2. Respect Mutuel des Limites</h2>
        <p>Chacun a droit à son espace personnel, ses amitiés et ses activités indépendantes.</p>

        <p><strong>À quoi ressemble le respect des limites :</strong></p>
        <ul>
          <li>Votre partenaire ne lit pas vos messages sans permission</li>
          <li>Vous pouvez passer du temps avec vos amis sans justification</li>
          <li>Les "non" sont acceptés sans culpabilisation</li>
          <li>Chacun a des activités personnelles</li>
          <li>L'intimité physique respecte toujours le consentement</li>
        </ul>

        <p><strong>Red flag :</strong> Jalousie excessive, contrôle de vos fréquentations, pression pour partager tous vos mots de passe, ou isolation de vos proches.</p>

        <h2>3. Confiance et Sécurité Émotionnelle</h2>
        <p>Vous vous sentez en sécurité d'être vulnérable sans craindre que ça soit utilisé contre vous.</p>

        <p><strong>Indicateurs de confiance :</strong></p>
        <ul>
          <li>Pas de vérification obsessionnelle du téléphone de l'autre</li>
          <li>Vos secrets partagés restent confidentiels</li>
          <li>Vous pouvez montrer vos émotions sans être ridiculisé(e)</li>
          <li>Les promesses sont tenues</li>
          <li>Honnêteté sur les relations passées et présentes</li>
        </ul>

        <p><strong>Red flag :</strong> Mensonges fréquents, comportements secrets, ou réactions disproportionnées quand vous posez des questions légitimes.</p>

        <h2>4. Soutien Mutuel Dans Les Objectifs</h2>
        <p>Un(e) bon(ne) partenaire encourage votre croissance personnelle et professionnelle.</p>

        <p><strong>Le soutien sain inclut :</strong></p>
        <ul>
          <li>Célébration sincère de vos succès</li>
          <li>Encouragement dans vos projets et ambitions</li>
          <li>Aide pratique quand vous en avez besoin</li>
          <li>Respect de vos choix de carrière</li>
          <li>Fierté de vos accomplissements, pas de jalousie</li>
        </ul>

        <p><strong>Red flag :</strong> Minimisation de vos réussites, découragement de vos ambitions, ou jalousie de votre succès.</p>

        <h2>5. Résolution Constructive des Conflits</h2>
        <p>Les désaccords sont inévitables, mais comment vous les gérez définit la santé de votre relation.</p>

        <p><strong>Gestion saine des conflits :</strong></p>
        <ul>
          <li>Attaquer le problème, pas la personne</li>
          <li>Écouter pour comprendre, pas pour répondre</li>
          <li>Recherche de compromis, pas de "gagner"</li>
          <li>Pas de hurlement, insultes ou violence</li>
          <li>Capacité à s'excuser sincèrement</li>
          <li>Les anciens conflits ne sont pas constamment ressortis</li>
        </ul>

        <p><strong>Red flag :</strong> Silent treatment (traitement silencieux), gaslighting ("tu es trop sensible"), refus de compromis, ou escalade vers l'agressivité.</p>

        <h2>6. Équilibre du Pouvoir et des Décisions</h2>
        <p>Les décisions importantes se prennent à deux, et les opinions des deux comptent également.</p>

        <p><strong>Équilibre sain :</strong></p>
        <ul>
          <li>Les décisions majeures (déménagement, finances) sont discutées</li>
          <li>Aucun partenaire ne domine constamment</li>
          <li>Partage équitable des tâches domestiques</li>
          <li>Les deux contribuent financièrement selon leurs moyens</li>
          <li>Compromis plutôt qu'ultimatums</li>
        </ul>

        <p><strong>Red flag :</strong> Un partenaire prend toutes les décisions importantes sans consultation, contrôle financier, ou distribution inéquitable des responsabilités.</p>

        <h2>7. Authenticité et Acceptation</h2>
        <p>Vous pouvez être vous-même sans prétendre ou cacher des parties de votre personnalité.</p>

        <p><strong>Signes d'acceptation mutuelle :</strong></p>
        <ul>
          <li>Aucune pression pour changer qui vous êtes</li>
          <li>Vos "bizarreries" sont aimées, pas tolérées</li>
          <li>Pas besoin de performer ou impressionner constamment</li>
          <li>Vos opinions sont respectées même en cas de désaccord</li>
          <li>On vous aime pour votre authenticité, pas pour une version idéalisée</li>
        </ul>

        <p><strong>Red flag :</strong> Critiques constantes, pression pour changer votre apparence/personnalité, ou comparaisons négatives avec d'autres personnes.</p>

        <h2>8. Vie Sexuelle Saine et Consentie</h2>
        <p>L'intimité physique est basée sur le désir mutuel et le consentement enthousiaste.</p>

        <p><strong>Intimité saine :</strong></p>
        <ul>
          <li>Consentement clair et enthousiaste toujours requis</li>
          <li>Communication ouverte sur les préférences</li>
          <li>Respect des refus sans bouderie ou culpabilisation</li>
          <li>Plaisir mutuel est une priorité</li>
          <li>Aucune pression ou coercition</li>
        </ul>

        <p><strong>Red flag :</strong> Pression pour des actes non désirés, bouderie après un refus, ou utilisation du sexe comme arme (récompense/punition).</p>

        <h2>9. Indépendance et Interdépendance</h2>
        <p>Une relation saine trouve le bon équilibre entre être ensemble et maintenir son individualité.</p>

        <p><strong>Équilibre sain :</strong></p>
        <ul>
          <li>Vous avez vos propres amis et hobbies</li>
          <li>Vous pouvez passer du temps seul(e) sans anxiété</li>
          <li>Votre identité ne se résume pas à la relation</li>
          <li>Vous vous soutenez mutuellement sans vous perdre</li>
          <li>Dépendance mutuelle, pas codépendance</li>
        </ul>

        <p><strong>Red flag :</strong> Fusion totale où vous perdez votre identité, ou au contraire, vie complètement séparée sans vraie connexion.</p>

        <h2>10. Croissance Ensemble</h2>
        <p>Vous évoluez ensemble plutôt que de stagner ou de vous éloigner.</p>

        <p><strong>Signes de croissance mutuelle :</strong></p>
        <ul>
          <li>Vous apprenez l'un de l'autre</li>
          <li>La relation vous rend meilleur(e)</li>
          <li>Vous partagez de nouveaux intérêts</li>
          <li>Adaptation aux changements de vie ensemble</li>
          <li>Réévaluation et ajustement réguliers de la relation</li>
        </ul>

        <h2>Que Faire Si Votre Relation Manque Ces Signes ?</h2>

        <p><strong>Si 1-2 signes manquent :</strong> C'est normal, aucune relation n'est parfaite. Identifiez les points d'amélioration et travaillez-y ensemble.</p>

        <p><strong>Si 3-5 signes manquent :</strong> C'est préoccupant. Une thérapie de couple pourrait aider à rétablir une dynamique saine.</p>

        <p><strong>Si 6+ signes manquent :</strong> Votre relation est probablement toxique. Consultez un(e) thérapeute individuellement pour évaluer la situation et envisager vos options.</p>

        <h2>Conclusion</h2>
        <p>Une relation saine n'est pas parfaite, mais elle est fondée sur le respect, la communication, la confiance et l'amour mutuel. Si votre relation présente la majorité de ces signes, vous êtes sur la bonne voie. Sinon, il n'est jamais trop tard pour travailler à l'améliorer ou prendre les décisions nécessaires pour votre bien-être.</p>

        <p>Vous cherchez une relation saine dès le départ ? <a href="/auth?mode=signup">Rejoignez A-More AI</a> et laissez notre IA vous aider à trouver quelqu'un avec qui construire une relation équilibrée et épanouissante.</p>
      `
    }
  }; */

  return (
    <>
      <Helmet>
        <title>{article.title} | Blog A-More AI</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={`${article.category}, rencontre, dating, A-More AI`} />
        <link rel="canonical" href={`${window.location.origin}/${language}/blog/${slug}`} />
      </Helmet>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.image,
        "datePublished": article.date,
        "dateModified": article.date,
        "articleSection": article.category,
        "inLanguage": language,
        "url": `${window.location.origin}/${language}/blog/${slug}`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${window.location.origin}/${language}/blog/${slug}`
        },
        "author": {
          "@type": "Organization",
          "name": article.author,
          "url": window.location.origin
        },
        "publisher": {
          "@type": "Organization",
          "name": "A-More AI",
          "logo": {
            "@type": "ImageObject",
            "url": `${window.location.origin}/logo.png`
          }
        }
      }} />

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "A-More AI",
            "item": `${window.location.origin}/${language}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": `${window.location.origin}/${language}/blog`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title,
            "item": `${window.location.origin}/${language}/blog/${slug}`
          }
        ]
      }} />

      <article className="min-h-screen py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(`/${language}/blog`)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au blog
          </button>

          <div className="glass-card overflow-hidden">
            <div className="relative h-96">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-pink-500 text-white text-sm font-semibold rounded-full">
                  {article.category}
                </span>
              </div>
            </div>

            <div className="p-8">
              <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {article.date}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {article.readTime} de lecture
                </div>
                <button 
                  onClick={async () => {
                    const url = window.location.href;
                    const title = article.title;
                    
                    // Check if Web Share API is supported (mobile devices)
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: title,
                          text: article.excerpt,
                          url: url
                        });
                        toast.success('Article partagé !');
                      } catch (error) {
                        // If user cancels, do nothing
                        if (error.name !== 'AbortError') {
                          console.error('Share failed:', error);
                        }
                      }
                    } else {
                      // Fallback: Copy to clipboard
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success('Lien copié dans le presse-papier !');
                      } catch (error) {
                        // If clipboard fails, show the URL in a prompt
                        const message = language === 'fr' ? 'Copiez ce lien :' : 
                                       language === 'es' ? 'Copie este enlace:' : 
                                       language === 'de' ? 'Kopieren Sie diesen Link:' : 
                                       'Copy this link:';
                        prompt(message, url);
                      }
                    }
                  }}
                  className="flex items-center gap-2 ml-auto text-pink-600 hover:text-pink-700 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Partager
                </button>
              </div>

              <h1 className="text-4xl font-bold text-gray-800 mb-6">{article.title}</h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {article.excerpt}
              </p>

              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
                style={{
                  color: '#4B5563',
                  lineHeight: '1.75'
                }}
              />

              {/* Email capture / signup CTA */}
              <div
                data-testid="article-cta-block"
                className="mt-12 rounded-3xl p-8 sm:p-10 text-center"
                style={{ background: 'linear-gradient(135deg, #ff6b9d 0%, #ff8e53 100%)' }}
              >
                <Heart className="w-10 h-10 text-white mx-auto mb-4" fill="white" />
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{cta.heading}</h2>
                <p className="text-white/90 text-base mb-6 max-w-xl mx-auto leading-relaxed">{cta.subtext}</p>
                <button
                  data-testid="article-cta-signup-button"
                  onClick={() => {
                    trackSignupClick('blog_article_cta');
                    navigate(`/${language}/auth?mode=signup`);
                  }}
                  className="inline-flex items-center gap-2 bg-white text-pink-600 font-semibold px-8 py-4 rounded-full shadow-lg hover:scale-105"
                  style={{ transition: 'transform 0.2s' }}
                >
                  <Heart className="w-5 h-5" />
                  {cta.button}
                </button>
                <p className="text-white/80 text-xs mt-4">{cta.note}</p>
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-4">Par {article.author}</p>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      trackSignupClick('blog_article_footer');
                      navigate(`/${language}/auth?mode=signup`);
                    }}
                    className="btn-primary px-6 py-3"
                  >
                    Rejoindre A-More AI
                  </button>
                  <button
                    onClick={() => navigate(`/${language}/blog`)}
                    className="btn-secondary px-6 py-3"
                  >
                    Lire plus d'articles
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
